// Catálogo de "vistas" para el modo Catálogo IA.
//
// Una "vista" es un encuadre/ángulo predefinido con el que el backend
// genera el producto. Existen dos categorías:
//
//   • con-modelo  → la vista es una POSE del catálogo de poses (tabla
//                   `poses`). Para Catálogo IA solo usamos poses de
//                   1 persona. Payload backend: `composicion: 'modelo'`
//                   + `modelo_mode: 'ia'` + `pose: [id]`.
//
//   • solo-producto → la vista es un ángulo del producto sin modelo,
//                     replicando la lista de `getVistaOptions()` de
//                     `PruebaNuevaInterfaz`. Payload backend:
//                     `composicion: 'producto'` + `vista_producto: [id]`.
//                     Solo aplicable cuando el producto tiene cierto
//                     `model_view` — no todas las vistas valen para
//                     todos los productos.
//
// El catálogo de vistas "solo-producto" depende de `model_view` del
// producto. Replicamos exactamente la lógica de PruebaNuevaInterfaz
// para que el backend reciba ids que ya sabe procesar.

import type { PresetV2 } from './mockData';

export type VistaCatalogoKind = 'con-modelo' | 'solo-producto' | 'mis-pose';

export interface VistaCatalogo {
  /** Id que viaja en el payload. Para con-modelo es el id de la pose;
   *  para solo-producto es el slug ('lateral', 'frente', etc.); para
   *  mis-pose es el id de la fila en `poses_personalizadas`. */
  id: string;
  /** Solo mis-pose: nombre del archivo del muñeco en storage
   *  (`<id_company>/poses_custom/<filename>`). Viaja en
   *  `poses_custom:[{id,filename}]` para que n8n baje el muñeco. */
  filename?: string;
  /** Nombre legible para mostrar en el picker / cards. */
  nombre: string;
  /** Imagen thumbnail default. Para con-modelo es la imagen de la pose;
   *  para solo-producto, una imagen de referencia (cuando exista). */
  imageUrl: string | null;
  /** Override por `model_view` — si el producto seleccionado matchea
   *  una key, se usa este URL en lugar del `imageUrl` default. Útil
   *  para vistas universales (`frente`, `posterior`) cuyo thumbnail
   *  conceptual cambia según la categoría: foto plana de zapatilla vs.
   *  foto plana de ropa, por ejemplo. */
  imageUrlByModelView?: Record<string, string>;
  /** Categoría a la que pertenece. */
  kind: VistaCatalogoKind;
  /** Solo aplica a solo-producto: lista de `model_view` con los que la
   *  vista es compatible. La vista se muestra como card solo si el
   *  producto matchea alguno de estos. `null` = compatible con todos. */
  compatibleModelViews?: string[] | null;
  /** Solo aplica a con-modelo (poses): "tipos de productos sugeridos"
   *  para esa pose — las áreas del cuerpo que destaca (`areas_principales`
   *  de la tabla `poses`), con el mismo vocabulario que el `model_view`
   *  de los productos (`calzado`, `torso`, `lentes`, `cabeza`, …). La pose
   *  se muestra/combina solo con productos cuyo `model_view` esté en esta
   *  lista — una pose que enfoca la cabeza no aplica a un zapato. Vacío o
   *  `undefined` = se trata como universal (row sin dato). */
  areasPrincipales?: string[];
}

const VISTA_BASE = '/images/multi-vistas/zapatillas';
const VISTA_BASE_OTROS = '/images/multi-vistas/otros';
const CLOTHING_MODEL_VIEWS = [
  'torso',
  'piernas',
  'cuerpo-completo',
  'cuerpo_completo',
];
/** Ropa donde el maniquí FEMENINO aporta algo: prendas que cubren el torso
 *  (busto/cintura) y prendas de cuerpo entero (vestidos, monos — figura
 *  completa). `piernas` queda afuera a propósito: en un jean o una pollera
 *  la vista no agrega sobre el maniquí neutro. */
const FEMALE_MANNEQUIN_MODEL_VIEWS = [
  'torso',
  'cuerpo-completo',
  'cuerpo_completo',
];
/** Cuando el preset / producto pertenece a una categoría de ropa,
 *  preferimos los thumbs en `/otros/` (foto plana real de prenda)
 *  sobre los `/zapatillas/` que vienen como default genérico. */
const clothingImageOverride = (fileName: string): Record<string, string> =>
  Object.fromEntries(
    CLOTHING_MODEL_VIEWS.map((mv) => [mv, `${VISTA_BASE_OTROS}/${fileName}`]),
  );

/**
 * Catálogo de vistas "solo-producto" — replica exactamente las
 * opciones que devuelve `getVistaOptions()` en PruebaNuevaInterfaz,
 * pero con metadatos de compatibilidad para que el card-builder pueda
 * filtrar inteligentemente.
 *
 * Ojo: los IDs son slugs (`'frente'`, `'lateral'`, etc.). En PruebaNueva
 * lo que se manda al backend es el slug, no un UUID — repetimos lo mismo
 * acá para no romper la API.
 */
export const VISTAS_SOLO_PRODUCTO: VistaCatalogo[] = [
  // Vistas de calzado/cabeza — con imágenes de referencia disponibles.
  {
    id: 'lateral',
    nombre: 'Lateral',
    imageUrl: `${VISTA_BASE}/lateral.webp`,
    kind: 'solo-producto',
    compatibleModelViews: ['calzado', 'cabeza'],
  },
  {
    id: 'superior',
    nombre: 'Superior',
    imageUrl: `${VISTA_BASE}/superior.webp`,
    kind: 'solo-producto',
    compatibleModelViews: ['calzado', 'cabeza'],
  },
  {
    id: 'perspectiva',
    nombre: 'Perspectiva',
    imageUrl: `${VISTA_BASE}/perspectiva.webp`,
    kind: 'solo-producto',
    compatibleModelViews: ['calzado', 'cabeza'],
  },
  // Vistas universales — plano frente y plano posterior aplican a casi
  // todo. Renombrados a "Plano frente / Plano posterior" para alinear
  // con la nomenclatura del modo personalizado de PruebaNueva — en ropa
  // distingue mejor el "plano sobre fondo" del "maniquí invisible". El
  // thumbnail default es la zapatilla; en categorías de ropa el override
  // muestra la foto plana de prenda (otros/plano_*).
  {
    id: 'frente',
    nombre: 'Plano frente',
    imageUrl: `${VISTA_BASE}/frente.webp`,
    imageUrlByModelView: clothingImageOverride('plano_delantero.jpeg'),
    kind: 'solo-producto',
    compatibleModelViews: null,
  },
  {
    id: 'posterior',
    nombre: 'Plano posterior',
    imageUrl: `${VISTA_BASE}/posterior.webp`,
    imageUrlByModelView: clothingImageOverride('plano_trasero.jpeg'),
    kind: 'solo-producto',
    compatibleModelViews: null,
  },
  // Maniquí invisible (ghost mannequin) — solo aplica a prendas. La
  // foto del producto sale "vestida" como si la llevara puesta una
  // persona pero sin persona visible. Tres ángulos: frontal, trasero y
  // 3/4 perspectiva. Como ya están restringidas a clothing-only,
  // ponemos directamente el thumb correspondiente en `imageUrl`.
  {
    id: 'maniqui_frontal',
    nombre: 'Maniquí invisible frontal',
    imageUrl: `${VISTA_BASE_OTROS}/maniqui_frente.jpeg`,
    kind: 'solo-producto',
    compatibleModelViews: CLOTHING_MODEL_VIEWS,
  },
  {
    id: 'maniqui_trasero',
    nombre: 'Maniquí invisible trasero',
    imageUrl: `${VISTA_BASE_OTROS}/maniqui_trasero.jpeg`,
    kind: 'solo-producto',
    compatibleModelViews: CLOTHING_MODEL_VIEWS,
  },
  {
    id: 'maniqui_angulo',
    nombre: 'Maniquí invisible ángulo',
    imageUrl: `${VISTA_BASE_OTROS}/maniqui_angulo.jpeg`,
    kind: 'solo-producto',
    compatibleModelViews: CLOTHING_MODEL_VIEWS,
  },
  // Maniquí invisible FEMENINO — mismo ghost mannequin pero con figura de
  // mujer marcando la caída de la tela. El prompt del backend
  // (`VISTA_DICTS.ropa`) adapta el shaping a la zona que la prenda cubre
  // (busto/cintura en un top, figura entera en un vestido) y es best-effort:
  // si la prenda es pesada o estructurada y el cuerpo femenino no cambiaría
  // la silueta, no fuerza curvas — y en ningún caso modifica el corte, largo,
  // estampas ni colores del producto.
  {
    id: 'maniqui_mujer_frontal',
    nombre: 'Maniquí femenino frontal',
    imageUrl: `${VISTA_BASE_OTROS}/maniqui_mujer_frente.jpeg`,
    kind: 'solo-producto',
    compatibleModelViews: FEMALE_MANNEQUIN_MODEL_VIEWS,
  },
  {
    id: 'maniqui_mujer_angulo',
    nombre: 'Maniquí femenino ángulo',
    imageUrl: `${VISTA_BASE_OTROS}/maniqui_mujer_angulo.jpeg`,
    kind: 'solo-producto',
    compatibleModelViews: FEMALE_MANNEQUIN_MODEL_VIEWS,
  },
  // Prenda doblada — flat lay de retail (prenda única doblada prolija sobre la
  // superficie). Aplica a toda la ropa: cualquier prenda se dobla.
  {
    id: 'prenda_doblada',
    nombre: 'Prenda doblada',
    imageUrl: `${VISTA_BASE_OTROS}/prenda_doblada.jpeg`,
    kind: 'solo-producto',
    compatibleModelViews: CLOTHING_MODEL_VIEWS,
  },
  // Vista automática — solo para productos non-clothing/non-shoe en
  // PruebaNueva. La dejamos pero la marcamos solo para esos
  // model_views.
  {
    id: 'automatica',
    nombre: 'Automática',
    imageUrl: null,
    kind: 'solo-producto',
    compatibleModelViews: [
      'accesorio_cuello',
      'manos',
      'lentes',
      'aros',
      'producto',
      'objeto',
    ],
  },
];

/** Prefijo de los ids de preset construidos desde una vista solo-producto
 *  (colección "Fondo blanco"). Espejo del prefijo `pose-` de las poses. */
export const VISTA_PRESET_PREFIX = 'vista-';

/**
 * Desdoblamiento de las vistas universales `frente`/`posterior` en la
 * colección "Fondo blanco": como en la grilla todavía NO hay producto
 * elegido, no podemos resolver el thumbnail/nombre dinámico (como hace
 * Catálogo IA vía `imageUrlByModelView`). Mostramos las DOS versiones
 * como cards separadas — mismo desdoblamiento que `getVistaOptions()`
 * de PruebaNuevaInterfaz: calzado ve "Frente" (foto zapatilla), ropa ve
 * "Plano frente" (foto prenda plana) — para que el user entienda cuál
 * aplica a su producto. El slug del payload es el MISMO en ambas
 * (`vistaProductoId`); solo cambian nombre, foto y sugeridos del picker.
 */
const FONDO_BLANCO_VISTA_VARIANTS: Record<
  string,
  { idSuffix: string; nombre: string; imageUrl: string; sugeridos: string[] }[]
> = {
  frente: [
    {
      idSuffix: '',
      nombre: 'Frente',
      imageUrl: `${VISTA_BASE}/frente.webp`,
      sugeridos: ['calzado', 'cabeza'],
    },
    {
      idSuffix: '-ropa',
      nombre: 'Plano frente',
      imageUrl: `${VISTA_BASE_OTROS}/plano_delantero.jpeg`,
      sugeridos: CLOTHING_MODEL_VIEWS,
    },
  ],
  posterior: [
    {
      idSuffix: '',
      nombre: 'Posterior',
      imageUrl: `${VISTA_BASE}/posterior.webp`,
      sugeridos: ['calzado', 'cabeza'],
    },
    {
      idSuffix: '-ropa',
      nombre: 'Plano posterior',
      imageUrl: `${VISTA_BASE_OTROS}/plano_trasero.jpeg`,
      sugeridos: CLOTHING_MODEL_VIEWS,
    },
  ],
};

/**
 * Cards de la colección virtual "Fondo blanco" (E-commerce) para las
 * vistas solo-producto. Un solo builder para que la home de InterfazV2,
 * InterfazV2Plantillas y ColeccionDetalle armen exactamente las mismas
 * cards (van DESPUÉS de las poses en la colección).
 *
 * Se excluye la vista sin thumbnail ('automatica' — imageUrl null):
 * quedaría una card en blanco, y además apunta a accesorios, no a los
 * rubros moda/belleza que ven esta colección.
 *
 * `frente`/`posterior` se desdoblan en 2 cards cada una (ver
 * `FONDO_BLANCO_VISTA_VARIANTS`); el resto sale 1:1 del catálogo.
 * `tiposProductoSugeridos` se mapea desde `compatibleModelViews` (o de
 * la variante) para que el picker de productos sugiera los compatibles.
 * `inputsAceptados: ['producto']` evita que el flow restaure/considere
 * un modelo (estas cards generan solo producto).
 */
export const buildVistasSoloProductoPresets = (): PresetV2[] =>
  VISTAS_SOLO_PRODUCTO.filter((v) => !!v.imageUrl).flatMap((v) => {
    const variants = FONDO_BLANCO_VISTA_VARIANTS[v.id];
    if (variants) {
      return variants.map(
        (variant): PresetV2 => ({
          id: `${VISTA_PRESET_PREFIX}${v.id}${variant.idSuffix}`,
          title: variant.nombre,
          imageUrl: variant.imageUrl,
          imageUrlFull: variant.imageUrl,
          kind: 'design-producto' as const,
          inputsAceptados: ['producto'],
          vistaProductoId: v.id,
          tiposProductoSugeridos: variant.sugeridos,
        }),
      );
    }
    return [
      {
        id: `${VISTA_PRESET_PREFIX}${v.id}`,
        title: v.nombre,
        imageUrl: v.imageUrl as string,
        imageUrlFull: v.imageUrl as string,
        kind: 'design-producto' as const,
        inputsAceptados: ['producto'],
        vistaProductoId: v.id,
        ...(v.compatibleModelViews && v.compatibleModelViews.length > 0
          ? { tiposProductoSugeridos: v.compatibleModelViews }
          : {}),
      },
    ];
  });

/**
 * Resuelve el thumbnail a usar para una vista según el contexto. Si la
 * vista tiene un `imageUrlByModelView` y el `modelView` provisto matchea
 * alguna key (normalizando `_`/`-`), usa ese override. Caso contrario,
 * cae al `imageUrl` default. Se le puede pasar una lista de model_views
 * (por ejemplo el `dominantModelView` calculado a partir de los productos
 * seleccionados) — el primero que matchee gana.
 */
export const resolveVistaImageUrl = (
  vista: VistaCatalogo,
  modelView: string | null | undefined,
): string | null => {
  if (!modelView || !vista.imageUrlByModelView) return vista.imageUrl;
  const norm = modelView.replace(/-/g, '_');
  for (const [key, url] of Object.entries(vista.imageUrlByModelView)) {
    if (key.replace(/-/g, '_') === norm) return url;
  }
  return vista.imageUrl;
};

/**
 * Chequea si una vista es compatible con el `model_view` de un producto.
 *
 *   • con-modelo (pose)   → mira `areasPrincipales` (tipos de producto
 *                           sugeridos de la pose). Una pose que enfoca la
 *                           cabeza no aplica a un zapato; una que enfoca
 *                           los pies sí.
 *   • solo-producto       → mira `compatibleModelViews`.
 *
 * Si la lista correspondiente está vacía o ausente (dato faltante), se
 * asume universal para no ocultar todo por un row incompleto.
 */
export const isVistaCompatibleConModelView = (
  vista: VistaCatalogo,
  modelView: string | null | undefined,
): boolean => {
  // mis-pose (poses custom del muñeco) son universales: el cliente las
  // creó a propósito, las combinamos con cualquier producto. No tienen
  // `areasPrincipales` ni `compatibleModelViews`.
  if (vista.kind === 'mis-pose') return true;
  const allowed =
    vista.kind === 'con-modelo'
      ? vista.areasPrincipales
      : vista.compatibleModelViews;
  if (!allowed || allowed.length === 0) return true;
  if (!modelView) {
    // Producto SIN model_view (tipo desconocido). No podemos descartar por
    // tipo, así que mostramos TODAS las vistas: las POSES (con-modelo) — igual
    // que PruebaNuevaInterfaz cuando falta el model_view — y TAMBIÉN todas las
    // vistas solo-producto (lateral, superior, maniquí, automática, etc.).
    // Decisión (ADR-114): ante un tipo desconocido preferimos ofrecer el
    // catálogo completo de "vistas del producto" en lugar de limitar a
    // frente/posterior; el usuario elige la que aplique. Los thumbs
    // categoría-específicos (o el `null` de "Automática") degradan a un
    // placeholder "sin preview", no rompen. Supersede la lógica previa que
    // solo dejaba pasar poses.
    return true;
  }
  // Normalizamos guion-medio vs guion-bajo (y case) porque algunos
  // productos vienen con 'cuerpo-completo' y otros con 'cuerpo_completo'.
  const norm = modelView.replace(/-/g, '_').toLowerCase();
  return allowed.some((c) => c.replace(/-/g, '_').toLowerCase() === norm);
};

/** Una vista solo-producto con el thumbnail YA resuelto para el producto
 *  elegido — lo que consume el picker inline del modo "Fotos de producto". */
export interface VistaProductoOption extends VistaCatalogo {
  /** Thumbnail resuelto vía `resolveVistaImageUrl` para el `model_view` del
   *  producto. `null` cuando la vista no tiene foto de ejemplo (Automática). */
  thumbUrl: string | null;
}

/**
 * Catálogo de vistas solo-producto aplicable a UN producto: las mismas
 * `VISTAS_SOLO_PRODUCTO` que ve Catálogo IA, filtradas por el `model_view` del
 * producto y con el thumbnail ya resuelto para esa categoría.
 *
 * Existe para que el modo "Fotos de producto" no repita el filter + resolve
 * que en Catálogo IA vive adentro de `VistaPickerFullscreen`. Un producto sin
 * `model_view` recibe el catálogo completo — misma decisión que
 * `isVistaCompatibleConModelView` (ADR-114).
 */
export const vistasSoloProductoPara = (
  modelView: string | null | undefined,
): VistaProductoOption[] =>
  VISTAS_SOLO_PRODUCTO.filter((v) =>
    isVistaCompatibleConModelView(v, modelView),
  ).map((v) => ({ ...v, thumbUrl: resolveVistaImageUrl(v, modelView) }));
